import { Empty, Flex, Pagination, Table } from 'antd'
import { TableProps } from 'antd/lib'

import { ROWS_PER_PAGE_OPTIONS } from 'constants/common'
import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import BaseSelect from '../BaseSelect'
import classNames from 'classnames'
import { ITableParams } from 'hooks/useTableParams'
import { SorterResult } from 'antd/es/table/interface'
import { ISort } from 'models/common'

interface IBaseTableProps<T extends object>
  extends TableProps<T>,
    Pick<ITableParams<T>, 'page' | 'rowsPerPage'>,
    Partial<Pick<ITableParams<T>, 'setPage' | 'setRowsPerPage' | 'setSort' | 'setSortBy'>> {
  className?: string
  total: number
  rowsPerPageOptions?: number[]
  showSizeChanger?: boolean
  showPagination?: boolean
  emptyText?: string
  triggerDesc?: string
  triggerAsc?: string
  cancelSort?: string
}

const BaseTable = <T extends object>({
  rowsPerPageOptions = ROWS_PER_PAGE_OPTIONS,
  showSizeChanger = true,
  showPagination = true,
  triggerDesc,
  triggerAsc,
  cancelSort,
  ...props
}: IBaseTableProps<T>) => {
  const { t } = useTranslation()

  const key = useId()
  const rowsPerPageOption = ROWS_PER_PAGE_OPTIONS.map((row) => {
    return {
      value: row,
      label: `${row} ${t('common.table.results')}`
    }
  })
  const handleChangeRowsPerPageOptions = (value: number) => {
    props.setRowsPerPage && props.setRowsPerPage(value)
  }

  const handlePageChange = (page: number) => {
    props.setPage && props.setPage(page)
  }

  const handleShowTotal = () => {
    const startIndex = (props.page - 1) * props.rowsPerPage + 1
    const endIndex =
      (props.page - 1) * props.rowsPerPage + props.rowsPerPage > props.total
        ? props.total
        : (props.page - 1) * props.rowsPerPage + props.rowsPerPage
    return (
      <span className='text-sm text-neutral-medium font-[450]'>{`${startIndex} - ${endIndex} ${t('common.table.of')} ${
        props.total
      } ${t('common.table.results')}`}</span>
    )
  }

  const handleTableChange = (_pagination: any, _filters: any, sorter: SorterResult<T>) => {
    if (!props.setSort || !props.setSortBy) return

    const SORT: { [key: string]: ISort } = {
      ascend: 'asc',
      descend: 'desc'
    }
    if (!!sorter.order && !!sorter.columnKey) {
      props.setSort(SORT[sorter.order])
      props.setSortBy(sorter.columnKey as keyof T)
    }
  }

  return (
    <div
      className={classNames('table-container mb-10 ', {
        'base-table': !showPagination
      })}
    >
      <Table
        locale={{
          triggerDesc: triggerDesc ? triggerDesc : '',
          triggerAsc: triggerAsc ? triggerAsc : '',
          cancelSort: cancelSort ? cancelSort : '',
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        }}
        sortDirections={['ascend', 'descend', 'ascend']}
        tableLayout='auto'
        dataSource={props.dataSource}
        pagination={
          props.total
            ? {
                pageSize: props.rowsPerPage,
                current: props.page,
                total: props.total,
                pageSizeOptions: rowsPerPageOptions,
                showSizeChanger,
                showTotal: handleShowTotal,
                onChange: (page: number, pageSize: number) => {
                  handleChangeRowsPerPageOptions(pageSize)
                  handlePageChange(page)
                }
              }
            : false
        }
        rowKey={key + 'id'}
        columns={props.columns}
        loading={props.loading}
        scroll={{ scrollToFirstRowOnChange: true, x: 'max-content' }}
        onChange={handleTableChange as any}
        {...props}
      />

      {showPagination && !!props.total && (
        <Flex className='justify-between p-3 border border-t-0 border-solid rounded-b-lg border-neutral-border-light'>
          <BaseSelect
            value={props.rowsPerPage}
            onChange={handleChangeRowsPerPageOptions}
            options={rowsPerPageOption}
            name={'rowsperPage'}
          />
          <Pagination
            pageSize={props.rowsPerPage}
            current={props.page}
            total={props.total}
            pageSizeOptions={rowsPerPageOptions}
            showTotal={handleShowTotal}
            showSizeChanger={false}
            showQuickJumper={false}
            onChange={(page: number) => {
              handlePageChange(page)
            }}
          />
        </Flex>
      )}
    </div>
  )
}

export default BaseTable
